// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect } from 'react';
import Layout from 'antd/lib/layout';
import Result from 'antd/lib/result';
import Spin from 'antd/lib/spin';
import notification from 'antd/lib/notification';

import AttributeAnnotationWorkspace from 'components/annotation-page/attribute-annotation-workspace/attribute-annotation-workspace';
import ReviewAnnotationsWorkspace from 'components/annotation-page/review-workspace/review-workspace';
import StandardWorkspaceComponent from 'components/annotation-page/standard-workspace/standard-workspace';
import StandardWorkspace3DComponent from 'components/annotation-page/standard3D-workspace/standard3D-workspace';
import TagAnnotationWorkspace from 'components/annotation-page/tag-annotation-workspace/tag-annotation-workspace';
import FiltersModalComponent from 'components/annotation-page/top-bar/filters-modal';
import StatisticsModalComponent from 'components/annotation-page/top-bar/statistics-modal';
import AnnotationTopBarContainer from 'containers/annotation-page/top-bar/top-bar';
import { Workspace } from 'reducers';
import { usePrevious } from 'utils/hooks';
import './styles.scss';
import Button from 'antd/lib/button';
import User from 'cvat-core/src/user';
import { Job } from 'cvat-core/src/session';
import { JobStage } from 'cvat-core/src/enums';
import { hasPermissionForJobAfterInspectionStage } from 'utils/permissions';

interface Props {
    user: any | null | undefined;
    job: any | null | undefined;
    fetching: boolean;
    frameNumber: number;
    workspace: Workspace;
    getJob(): void;
    saveLogs(): void;
    closeJob(): void;
    changeFrame(frame: number): void;
}

function jobLocked(user: User, job: Job): boolean {
    if (job.locked) {
        return true;
    }

    if (user.isSuperuser) {
        return false;
    }

    if (hasPermissionForJobAfterInspectionStage(user)) {
        return false;
    }

    if (job.stage !== JobStage.ANNOTATION && job.stage !== JobStage.VALIDATION) {
        return true;
    }

    return false;
}

export default function AnnotationPageComponent(props: Props): JSX.Element {
    const {
        user, job, fetching, workspace, frameNumber, getJob, closeJob, saveLogs, changeFrame,
    } = props;
    const prevJob = usePrevious(job);
    const prevFetching = usePrevious(fetching);

    useEffect(() => {
        saveLogs();
        const root = window.document.getElementById('root');
        if (root) {
            root.style.minHeight = '720px';
        }

        return () => {
            saveLogs();
            closeJob();

            if (root) {
                root.style.minHeight = '';
            }
        };
    }, []);

    useEffect(() => {
        if (job === null && !fetching) {
            getJob();
        }
    }, [job, fetching]);

    useEffect(() => {
        if (prevFetching && !fetching && !prevJob && job) {
            const latestFrame = localStorage.getItem(`Job_${job.id}_frame`);
            if (latestFrame && Number.isInteger(+latestFrame)) {
                const parsedFrame = +latestFrame;
                if (parsedFrame !== frameNumber && parsedFrame >= job.startFrame && parsedFrame <= job.stopFrame) {
                    const notificationKey = `cvat-notification-continue-job-${job.id}`;
                    notification.info({
                        key: notificationKey,
                        message: `你已经完成 #${parsedFrame} 帧标注工作。`,
                        duration: null,
                        description: (
                            <span>
                                如果你想继续，点击
                                <Button
                                    className='cvat-notification-continue-job-button'
                                    type='link'
                                    onClick={() => {
                                        changeFrame(parsedFrame);
                                        notification.close(notificationKey);
                                    }}
                                >
                                    这里
                                </Button>
                            </span>
                        ),
                        placement: 'topRight',
                        className: 'cvat-notification-continue-job',
                    });
                }
            }

            if (!job.labels.length) {
                notification.warning({
                    message: '没有标签',
                    description: (
                        <span>
                            {`${job.projectId ? '项目' : '任务'} ${
                                job.projectId || job.taskId
                            } 不包含任何标签。 `}
                            <a href={`/${job.projectId ? 'projects' : 'tasks'}/${job.projectId || job.taskId}/`}>
                                添加
                            </a>
                            {' 标签进行标注。'}
                        </span>
                    ),
                    placement: 'topRight',
                    className: 'cvat-notification-no-labels',
                });
            }
        }
    }, [job, fetching, prevJob, prevFetching]);

    if (job === null) {
        return <Spin size='large' className='cvat-spinner' />;
    }

    if (typeof job === 'undefined') {
        return (
            <Result
                className='cvat-not-found'
                status='404'
                title='抱歉，当前作业不可访问'
                subTitle='请确认要访问的资源存在或者有权访问'
            />
        );
    }

    if (jobLocked(user, job)) {
        const handleKeyDown = (event: any) => {
            event.preventDefault();
        };
        window.addEventListener('keydown', handleKeyDown);
    }

    return (
        <>
            <Layout className='cvat-annotation-page'>
                <Layout.Header className='cvat-annotation-header'>
                    <AnnotationTopBarContainer />
                </Layout.Header>
                <Layout.Content className='cvat-annotation-layout-content'>
                    <div>
                        {jobLocked(user, job) && (
                            <div className='cvat-annotation-page-overlay'>
                                <div className='cvat-annotation-page-overlay-content'>
                                    <h1>当前批次已被锁定</h1>
                                </div>
                            </div>
                        )}
                    </div>
                    {workspace === Workspace.STANDARD3D && <StandardWorkspace3DComponent />}
                    {workspace === Workspace.STANDARD && <StandardWorkspaceComponent />}
                    {workspace === Workspace.ATTRIBUTE_ANNOTATION && <AttributeAnnotationWorkspace />}
                    {workspace === Workspace.TAG_ANNOTATION && <TagAnnotationWorkspace />}
                    {workspace === Workspace.REVIEW_WORKSPACE && <ReviewAnnotationsWorkspace />}
                </Layout.Content>
                <FiltersModalComponent />
                <StatisticsModalComponent />
            </Layout>
        </>
    );
}
